import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * One block of help copy: an optional bold sub-heading followed by its
 * paragraph text. Screens with a single explanatory paragraph just omit
 * `heading`; screens that group several explanations (e.g. the settings
 * screen) pass one section per group. `text` may contain `\n` line breaks —
 * HelpDialog renders them via `white-space: pre-line`.
 */
export interface HelpSection {
  heading?: string
  text: string
}

/**
 * Content for a single help dialog. `title` is the heading; `body` is the
 * ordered list of sections. Both are passed in already-translated — the copy
 * lives in each screen's own <i18n> block so help text sits next to the
 * screen it explains.
 */
export interface HelpContent {
  title: string
  body: HelpSection[]
}

/**
 * Single source of truth for the global help dialog. App.vue mounts one
 * <HelpDialog/> and binds its visibility here; any screen surfaces its own
 * help by calling `useHelpStore().openHelp({ title, body })` — typically
 * from the circular "?" button in its header (Navigation emits `help`, or
 * a screen wires its own #actions slot button).
 *
 * Mirrors the entitlement store's paywall pattern (openPaywall / paywallOpen
 * + a single global <PurchaseDialog/>).
 */
export const useHelpStore = defineStore('help', () => {
  const open = ref(false)
  const title = ref('')
  const body = ref<HelpSection[]>([])

  function openHelp(content: HelpContent): void {
    title.value = content.title
    body.value = content.body
    open.value = true
  }

  function close(): void {
    open.value = false
  }

  return { open, title, body, openHelp, close }
})
